import { env } from '@/lib/env/client'
import canUseDOM from '@/payload/utils/canUseDOM'
import { getClientSideURL, getServerSideURL } from '@/payload/utils/getURL'

/**
 * Processes media resource URL to ensure proper formatting
 * @param url The original URL from the resource
 * @param cacheTag Optional cache tag to append to the URL
 * @returns Properly formatted URL with cache tag if provided
 */
export const getMediaUrl = (
  url: string | null | undefined,
  cacheTag?: string | null
): string => {
  if (!url) return ''

  // Check if URL already has http/https protocol
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return cacheTag ? `${url}?${encodeURIComponent(cacheTag)}` : url
  }

  const s3BucketUrl = env.NEXT_PUBLIC_HETZNER_S3_BUCKET_URL
  const s3Env = env.NEXT_PUBLIC_HETZNER_S3_ENV

  let finalUrl: string

  if (s3BucketUrl) {
    const prefix = s3Env ? `cms-${s3Env}` : ''
    const cleanUrl = url.startsWith('/') ? url : `/${url}`

    // Ensure we don't have double prefix if url already contains it
    const urlWithPrefix =
      prefix && !cleanUrl.startsWith(`/${prefix}/`) ? `/${prefix}${cleanUrl}` : cleanUrl

    // Remove double slashes and join with bucket URL
    const normalizedPath = urlWithPrefix.replace(/\/+/g, '/')
    finalUrl = `${s3BucketUrl.replace(/\/$/, '')}${normalizedPath}`
  } else {
    // Otherwise prepend appropriate base URL
    const baseUrl = canUseDOM ? getClientSideURL() : getServerSideURL()
    finalUrl = `${baseUrl.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}`
  }

  if (cacheTag && cacheTag !== '') {
    const separator = finalUrl.includes('?') ? '&' : '?'
    return `${finalUrl}${separator}${encodeURIComponent(cacheTag)}`
  }

  return finalUrl
}
